import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { SUBMIT_GUARANTOR } from 'graphql/resolvers/admin/candidates/mutations';
import FormUploader from 'components/Uploader';
import {
  GUARANTOR_SUBMISSION,
  GET_CANDIDATE_VERIFICATION_REQUEST
} from 'graphql/resolvers/client/queries';
import { guarantorSubmitValidationSchema } from 'helpers/validators';
import { toastSuccess, toastError } from 'components/common/Toast';
import Loader from 'components/common/Loader';
import { svgs } from 'assets';
import GuarantorReport from 'components/VerificationReports/GuarantorReport';

// --- Warm, guided write-form palette & styles ---
// This is the form a guarantor (a stranger, often on a phone) fills out to
// vouch for a candidate. Distinct from the internal report: friendlier,
// reassuring, one clear question at a time.
const form = {
  ink: '#22303f',
  slate: '#6b7d8f',
  paper: '#faf7f0',
  cream: '#fffdf9',
  line: '#e7e0d0',
  teal: '#408996',
  tealDeep: '#2f6771',
  tealTint: 'rgba(64, 137, 150, 0.08)',
  amber: '#c8842d',
  blue: '#007bff',
  blueDeep: '#0062cc',
  blueTint: 'rgba(0, 123, 255, 0.08)'
};

const formStyles = {
  wrapper: {
    background: form.paper,
    minHeight: '100vh',
    padding: '32px 16px 90px'
  },
  shell: { maxWidth: '760px', margin: '0 auto' },
  formCard: {
    background: form.cream,
    borderRadius: '12px',
    border: `1px solid ${form.line}`,
    boxShadow: '0 1px 2px rgba(20, 30, 40, 0.04)',
    overflow: 'hidden'
  },
  headerCard: {
    background: form.blueTint,
    borderBottom: `1px solid rgba(0, 123, 255, 0.18)`,
    padding: '30px 30px 26px'
  },
  brandBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '22px'
  },
  brandLogo: { width: '52px', height: '52px', flexShrink: 0 },
  brandName: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: form.ink,
    letterSpacing: '0.2px'
  },
  intro: { textAlign: 'center' },
  eyebrow: {
    color: form.tealDeep,
    fontSize: '1.6rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    marginBottom: '12px'
  },
  subtitle: {
    color: form.ink,
    fontSize: '1.4rem',
    lineHeight: '1.55',
    maxWidth: '560px',
    margin: '0 auto'
  },
  progressTrack: {
    display: 'flex',
    gap: '5px',
    marginTop: '20px'
  },
  progressSegment: (filled) => ({
    flex: 1,
    height: '4px',
    borderRadius: '999px',
    background: filled ? form.teal : form.line
  }),
  progressLabel: {
    color: form.slate,
    fontSize: '1.1rem',
    fontWeight: '600',
    marginTop: '10px',
    letterSpacing: '0.3px'
  },
  card: {
    padding: '24px 0',
    borderBottom: `1px solid ${form.line}`
  },
  cardLast: {
    padding: '24px 0'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '18px'
  },
  cardIndex: {
    flexShrink: 0,
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: form.tealTint,
    color: form.teal,
    fontWeight: '700',
    fontSize: '1.15rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  question: {
    fontSize: '1.35rem',
    fontWeight: '600',
    color: form.ink,
    lineHeight: '1.45'
  },
  helperText: {
    fontSize: '1.1rem',
    color: form.amber,
    fontWeight: '500',
    marginTop: '4px'
  },
  fieldLabel: {
    width: '90%',
    margin: '0 auto 8px',
    fontSize: '1.15rem',
    fontWeight: '600',
    color: form.slate,
    display: 'block'
  },
  required: { color: '#c0392b', marginLeft: '3px' },
  input: {
    width: '90%',
    margin: '0 auto',
    display: 'block',
    padding: '13px 14px',
    borderRadius: '8px',
    border: `1.5px solid ${form.line}`,
    fontSize: '1.3rem',
    color: form.ink,
    background: '#fff'
  },
  choiceGrid: (columns = 'auto-fit') => ({
    display: 'grid',
    gridTemplateColumns:
      columns === 'auto-fit' ? 'repeat(auto-fit, minmax(150px, 1fr))' : columns,
    gap: '10px',
    width: '90%',
    margin: '0 auto'
  }),
  choice: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '13px 16px',
    borderRadius: '8px',
    border: `1.5px solid ${selected ? form.blue : form.line}`,
    background: selected ? form.blue : '#fff',
    color: selected ? '#fff' : form.ink,
    fontWeight: selected ? '600' : '500',
    fontSize: '1.22rem',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background 0.15s ease'
  }),
  commentLabel: {
    width: '90%',
    fontSize: '1.15rem',
    fontWeight: '600',
    color: form.slate,
    margin: '16px auto 8px',
    display: 'block'
  },
  errorText: {
    width: '90%',
    margin: '8px auto 0',
    color: '#c0392b',
    fontSize: '1.08rem',
    fontWeight: '500'
  },
  divider: { borderTop: `1px solid ${form.line}`, margin: '30px 0' },
  photoFrame: {
    width: '220px',
    height: '220px',
    borderRadius: '14px',
    border: `2px dashed ${form.line}`,
    objectFit: 'cover',
    background: '#fff'
  },
  photoFrameActive: {
    width: '220px',
    height: '220px',
    borderRadius: '14px',
    border: `2px solid ${form.teal}`,
    objectFit: 'cover',
    background: '#000'
  },
  button: {
    padding: '12px 22px',
    borderRadius: '8px',
    border: `1.5px solid ${form.blue}`,
    background: '#fff',
    color: form.blue,
    fontWeight: '700',
    fontSize: '1.2rem',
    cursor: 'pointer'
  },
  buttonSolid: {
    padding: '12px 22px',
    borderRadius: '8px',
    border: `1.5px solid ${form.blue}`,
    background: form.blue,
    color: '#fff',
    fontWeight: '700',
    fontSize: '1.2rem',
    cursor: 'pointer'
  },
  buttonDanger: {
    padding: '12px 22px',
    borderRadius: '8px',
    border: '1.5px solid #c0392b',
    background: '#fff',
    color: '#c0392b',
    fontWeight: '700',
    fontSize: '1.2rem',
    cursor: 'pointer'
  },
  buttonSmall: {
    padding: '9px 16px',
    borderRadius: '8px',
    border: `1.5px solid ${form.blue}`,
    background: form.blue,
    color: '#fff',
    fontWeight: '700',
    fontSize: '1.05rem',
    cursor: 'pointer'
  },
  buttonSmallOutline: {
    padding: '9px 16px',
    borderRadius: '8px',
    border: `1.5px solid ${form.blue}`,
    background: '#fff',
    color: form.blue,
    fontWeight: '700',
    fontSize: '1.05rem',
    cursor: 'pointer'
  },
  agreementCard: {
    background: form.blueTint,
    border: `1px solid rgba(0, 123, 255, 0.25)`,
    borderRadius: '10px',
    padding: '20px 22px',
    marginBottom: '20px',
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start'
  },
  submitBar: { display: 'flex', justifyContent: 'flex-end' },
  submitButton: {
    padding: '15px 34px',
    borderRadius: '9px',
    border: 'none',
    background: form.blue,
    color: '#fff',
    fontWeight: '700',
    fontSize: '1.32rem',
    letterSpacing: '0.3px',
    cursor: 'pointer',
    boxShadow: '0 10px 22px -8px rgba(0, 98, 204, 0.45)'
  },
  uploadCardDone: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px 18px',
    borderRadius: '10px',
    border: `1px solid ${form.line}`,
    background: '#fff'
  },

  // --- Read-only report-specific additions ---
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '999px',
    background: 'rgba(0, 123, 255, 0.12)',
    border: `1px solid rgba(0, 123, 255, 0.35)`,
    color: form.blueDeep,
    fontSize: '0.85rem',
    fontWeight: '700',
    letterSpacing: '0.3px'
  },
  summaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    borderBottom: `1px solid ${form.line}`
  },
  summaryCell: {
    padding: '22px 26px',
    borderRight: `1px solid ${form.line}`
  },
  summaryLabel: {
    fontSize: '0.95rem',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: form.slate,
    marginBottom: '6px'
  },
  summaryValue: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: form.ink,
    wordBreak: 'break-word'
  },
  rowLabel: {
    fontSize: '1rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    color: form.slate,
    marginBottom: '6px'
  },
  questionLabel: {
    fontSize: '1.15rem',
    fontWeight: '600',
    color: form.ink,
    marginBottom: '12px'
  },
  rowValue: {
    fontSize: '1.16rem',
    fontWeight: '600',
    color: form.ink,
    wordBreak: 'break-word'
  },
  answerCard: {
    border: `1px solid ${form.line}`,
    borderRadius: '10px',
    padding: '18px 20px',
    background: '#fbfaf7'
  },
  verdictPill: (active) => ({
    display: 'inline-block',
    padding: '9px 18px',
    borderRadius: '8px',
    fontSize: '1.08rem',
    fontWeight: '700',
    border: `1.5px solid ${active ? form.blue : form.line}`,
    background: active ? form.blue : '#fff',
    color: active ? '#fff' : form.slate
  }),
  commentBlock: {
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: `1px dashed ${form.line}`,
    fontSize: '1.16rem',
    color: form.ink,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap'
  },
  commentSubLabel: {
    fontSize: '1rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    textTransform: 'none',
    color: form.slate,
    marginBottom: '6px'
  },
  footerNote: {
    marginTop: '10px',
    paddingTop: '20px',
    borderTop: `1px solid ${form.line}`,
    fontSize: '1rem',
    color: '#000',
    textAlign: 'center'
  }
};

const GuarantorForm = ({
  match: { params },
  employeeName: defaultEmployeeName = 'The Candidate'
}) => {
  const requestId = params.id;
  const verificationId = params?.verificationId;

  const [showLoader, setLoaderState] = useState(false);

  // Camera & Capture State
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);

  const [submitGuarantorForm] = useMutation(SUBMIT_GUARANTOR);

  const { data: submissionData, refetch: refetchSubmission } = useQuery(
    GUARANTOR_SUBMISSION,
    {
      variables: {
        guarantorId: requestId,
        verificationId: verificationId
      },
      skip: !requestId
    }
  );

  const { data: candidateData } = useQuery(GET_CANDIDATE_VERIFICATION_REQUEST, {
    variables: { id: verificationId },
    skip: !verificationId
  });

  const candidate = candidateData?.verificationRequest?.candidate;
  const candidateName = candidate
    ? `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim()
    : '';
  const employeeName = candidateName || defaultEmployeeName;

  const isReadOnly = !!submissionData?.guarantorSubmission;

  const parsedExtraData = submissionData?.guarantorSubmission?.formData
    ? JSON.parse(submissionData?.guarantorSubmission?.formData)
    : {};

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // --- Camera Control Functions ---
  const getCameraErrorMessage = (err) => {
    switch (err?.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Camera permission was denied. Please allow camera access for this site in your browser settings, then reload the page.';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'No camera was found on this device.';
      case 'NotReadableError':
      case 'TrackStartError':
        return 'Your camera is already in use by another app. Please close any other app using the camera and try again.';
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return "Your camera doesn't support the requested settings.";
      case 'SecurityError':
        return 'Camera access requires a secure (https) connection.';
      default:
        return 'Unable to access camera. Please check permissions.';
    }
  };

  const startCamera = async () => {
    if (!window.isSecureContext) {
      toastError(
        svgs.DELETE_ICON,
        'Camera access requires a secure connection. Please make sure the link starts with https:// and try again.'
      );
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toastError(
        svgs.DELETE_ICON,
        "Camera access isn't supported in this browser. Please open this link in Chrome or Safari (not inside an app like WhatsApp, Facebook, or Gmail) and try again."
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: false
      });
      mediaStreamRef.current = stream;
      setIsCameraActive(true);
    } catch (err) {
      // Some mobile cameras reject the ideal resolution/facingMode combo;
      // retry with no constraints before giving up.
      if (
        err?.name === 'OverconstrainedError' ||
        err?.name === 'ConstraintNotSatisfiedError'
      ) {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false
          });
          mediaStreamRef.current = fallbackStream;
          setIsCameraActive(true);
          return;
        } catch (fallbackErr) {
          // eslint-disable-next-line no-console
          console.error('[GuarantorForm] getUserMedia (fallback) failed:', fallbackErr);
          toastError(svgs.DELETE_ICON, getCameraErrorMessage(fallbackErr));
          return;
        }
      }
      // eslint-disable-next-line no-console
      console.error('[GuarantorForm] getUserMedia failed:', err);
      toastError(svgs.DELETE_ICON, getCameraErrorMessage(err));
    }
  };

  // The <video> element only mounts once isCameraActive is true, so the
  // stream must be attached after that render, not inside startCamera.
  useEffect(() => {
    if (isCameraActive && videoRef.current && mediaStreamRef.current) {
      videoRef.current.srcObject = mediaStreamRef.current;
    }
  }, [isCameraActive]);

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const takePhoto = (setFieldValue) => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setCapturedImage(imageDataUrl);

      // Converts to an actual File instance (guarantor_photo.jpg)
      const photoFile = dataURLtoFile(imageDataUrl, 'guarantor_photo.jpg');
      setFieldValue('photo', photoFile);

      stopCamera();
    }
  };

  const clearPhoto = (setFieldValue) => {
    setCapturedImage(null);
    setFieldValue('photo', '');
    startCamera();
  };

  const options = {
    income: [
      'N2M to N5M Per annum',
      'N5M to N7M Per annum',
      'N7M to N9M Per annum',
      'N9m to N12M Per annum',
      'N12m to N15M Per annum',
      'Above N15M Per annum'
    ],
    relationship: ['Professional', 'Family', 'Friend', 'Others'],
    duration: ['Less than 3years', '3-5 years', '5-10 years', 'Over 10 years'],
    character: ['Very good', 'Good', 'Satisfactory', 'Unsatisfactory'],
    honest: ['Definitely', 'To a large extent', 'Not Certain', 'No Comment'],
    recommend: ['Yes', 'No'],
    chosenToStand: ['Yes', 'No']
  };

  const handleOnSubmit = async (values) => {
    setLoaderState(true);
    try {
      const response = await submitGuarantorForm({
        variables: {
          input: {
            requestId: requestId,
            fullname: values.guarantorName,
            email: values.email,
            phone: values.phone,
            address: values.workAddress,
            idCard: values.idCard,
            photo: values.photo,
            formData: JSON.stringify({
              guarantorFullName: values.guarantorName,
              workPlaceNameAddress: values.workAddress,
              incomeRange: values.incomeRange,
              relationship: values.relationship,
              relationshipOthers: values.relationshipOthers,
              durationKnown: values.durationKnown,
              durationComments: values.durationComments,
              conductCharacter: values.conductCharacter,
              conductComments: values.conductComments,
              isHonest: values.isHonest,
              recommendEmployment: values.recommendEmployment,
              recommendComments: values.recommendComments,
              chosenToStand: values.chosenToStand,
              generalComments: values.generalComments
            })
          }
        }
      });

      if (response.data?.submitGuarantorForm?.ok) {
        toastSuccess(svgs.SUCCESS_ICON, 'Guarantor Form Submitted Successfully');
        if (refetchSubmission) {
          await refetchSubmission();
        }
      } else {
        const errors = response.data?.submitGuarantorForm?.errors?.messages.join(', ');
        toastError(svgs.DELETE_ICON, errors || 'Submission failed.');
      }
    } catch (e) {
      const errors = e?.data?.submitGuarantorForm?.errors?.messages.join(', ');
      toastError(svgs.DELETE_ICON, errors || 'An error occurred during submission.');
    } finally {
      setLoaderState(false);
    }
  };

  if (isReadOnly) {
    return (
      <GuarantorReport
        submission={submissionData?.guarantorSubmission}
        extra={parsedExtraData}
        employeeName={employeeName}
      />
    );
  }

  const totalQuestions = 9;
  const answeredCount = (values) =>
    [
      values.guarantorName,
      values.workAddress,
      values.incomeRange,
      values.relationship,
      values.durationKnown,
      values.conductCharacter,
      values.isHonest,
      values.recommendEmployment,
      values.chosenToStand
    ].filter(Boolean).length;

  return (
    <div style={formStyles.wrapper}>
      <Formik
        enableReinitialize
        initialValues={{
          guarantorName: submissionData?.guarantorSubmission?.fullname || '',
          workAddress: submissionData?.guarantorSubmission?.address || '',
          incomeRange: parsedExtraData.incomeRange || '',
          relationship: parsedExtraData.relationship || '',
          relationshipOthers: parsedExtraData.relationshipOthers || '',
          durationKnown: parsedExtraData.durationKnown || '',
          durationComments: parsedExtraData.durationComments || '',
          conductCharacter: parsedExtraData.conductCharacter || '',
          conductComments: parsedExtraData.conductComments || '',
          isHonest: parsedExtraData.isHonest || '',
          recommendEmployment: parsedExtraData.recommendEmployment || '',
          recommendComments: parsedExtraData.recommendComments || '',
          chosenToStand: parsedExtraData.chosenToStand || '',
          generalComments: parsedExtraData.generalComments || '',
          idCard: null,
          photo: '',
          phone: submissionData?.guarantorSubmission?.phone || '',
          email: submissionData?.guarantorSubmission?.email || '',
          agreement: false
        }}
        validationSchema={guarantorSubmitValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values, setFieldValue }) => (
          <div style={formStyles.shell}>
            <Form>
              <div style={formStyles.formCard}>
                <div style={formStyles.headerCard}>
                  <div style={formStyles.brandBar}>
                    <svgs.LOGO style={formStyles.brandLogo} />
                    <span style={formStyles.brandName}>Workforce Group</span>
                  </div>
                  <div style={formStyles.intro}>
                    <p style={formStyles.eyebrow}>Guarantor Form</p>
                    <p style={formStyles.subtitle}>
                      Please answer every question as honestly as possible. Your responses
                      are treated with the highest level of confidentiality and shared
                      only with the verifying employer.
                    </p>
                    <div style={formStyles.progressTrack}>
                      {Array.from({ length: totalQuestions }).map((_, i) => (
                        <div
                          key={i}
                          style={formStyles.progressSegment(i < answeredCount(values))}
                        />
                      ))}
                    </div>
                    <p style={formStyles.progressLabel}>
                      {answeredCount(values)} of {totalQuestions} questions answered
                    </p>
                  </div>
                </div>

                {/* REFEREE PHOTO CAPTURE SECTION */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>📷</span>
                    <div>
                      <p style={formStyles.question}>
                        A quick photo of yourself{' '}
                        <span style={formStyles.required}>*</span>
                      </p>
                      <p
                        style={{
                          ...formStyles.helperText,
                          color: form.ink,
                          fontWeight: 500,
                          lineHeight: '1.6',
                          maxWidth: '90%'
                        }}
                      >
                        Capture a clear, well-lit photo of your face. Remove glasses,
                        hats, or any covering, and ensure your features are fully visible.
                        This image will be matched against the valid means of
                        identification you provided.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex flex-column align-items-center gap-3">
                    <canvas ref={canvasRef} style={{ display: 'none' }} />

                    {capturedImage ? (
                      <img
                        src={capturedImage}
                        alt="Captured guarantor"
                        style={formStyles.photoFrame}
                      />
                    ) : isCameraActive ? (
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={formStyles.photoFrameActive}
                      />
                    ) : (
                      <div
                        style={{
                          ...formStyles.photoFrame,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: form.slate,
                          fontSize: '1.05rem',
                          fontWeight: '600',
                          textAlign: 'center',
                          padding: '10px'
                        }}
                      >
                        Camera is off
                      </div>
                    )}

                    <div className="d-flex gap-2" style={{ marginTop: '12px' }}>
                      {!isCameraActive && !capturedImage && (
                        <button
                          type="button"
                          style={formStyles.buttonSmall}
                          onClick={startCamera}
                        >
                          Start
                        </button>
                      )}

                      {isCameraActive && !capturedImage && (
                        <div className="d-flex gap-4">
                          <button
                            type="button"
                            style={formStyles.buttonSmall}
                            onClick={() => takePhoto(setFieldValue)}
                          >
                            Capture
                          </button>
                          <button
                            type="button"
                            style={formStyles.buttonSmallOutline}
                            onClick={stopCamera}
                          >
                            Close
                          </button>
                        </div>
                      )}

                      {capturedImage && (
                        <button
                          type="button"
                          style={formStyles.buttonDanger}
                          onClick={() => clearPhoto(setFieldValue)}
                        >
                          Retake Photo
                        </button>
                      )}
                    </div>
                    <ErrorMessage
                      name="photo"
                      component="div"
                      style={formStyles.errorText}
                    />
                  </div>
                </div>

                {/* 1. Guarantor Name */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>1</span>
                    <p style={formStyles.question}>
                      What is your full name? <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <Field
                    name="guarantorName"
                    type="text"
                    placeholder="e.g. Adaeze Nwosu"
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="guarantorName"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* 2. Place of Work */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>2</span>
                    <p style={formStyles.question}>
                      What is your current place of work or business name and address?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <Field
                    name="workAddress"
                    as="textarea"
                    rows="3"
                    placeholder="Company/business name, and full address"
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="workAddress"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* Contact Block */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>📞</span>
                    <p style={formStyles.question}>How can we reach you?</p>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={formStyles.fieldLabel}>
                        Telephone Number <span style={formStyles.required}>*</span>
                      </label>
                      <Field
                        name="phone"
                        type="tel"
                        placeholder="080..."
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={formStyles.fieldLabel}>
                        Email Address <span style={formStyles.required}>*</span>
                      </label>
                      <Field
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Income Range */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>3</span>
                    <p style={formStyles.question}>
                      What is your income range?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid()}>
                    {options.income.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.incomeRange === opt)}
                      >
                        <Field
                          type="radio"
                          name="incomeRange"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="incomeRange"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* 4. Relationship */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>4</span>
                    <p style={formStyles.question}>
                      What is your relationship with the candidate?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(130px, 1fr))')}
                  >
                    {options.relationship.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.relationship === opt)}
                      >
                        <Field
                          type="radio"
                          name="relationship"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  {values.relationship === 'Others' && (
                    <div className="mt-3">
                      <label style={formStyles.fieldLabel}>Please specify</label>
                      <Field
                        name="relationshipOthers"
                        type="text"
                        style={formStyles.input}
                      />
                      <ErrorMessage
                        name="relationshipOthers"
                        component="div"
                        style={formStyles.errorText}
                      />
                    </div>
                  )}
                  <ErrorMessage
                    name="relationship"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* 5. How Long Known */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>5</span>
                    <div>
                      <p style={formStyles.question}>
                        How long have you known the candidate?{' '}
                        <span style={formStyles.required}>*</span>
                      </p>
                      <p style={{ ...formStyles.helperText, color: '#c0392b' }}>
                        Please don&apos;t stand as a guarantor for someone you don&apos;t
                        know well.
                      </p>
                    </div>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(150px, 1fr))')}
                  >
                    {options.duration.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.durationKnown === opt)}
                      >
                        <Field
                          type="radio"
                          name="durationKnown"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="durationKnown"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>Comments (optional)</label>
                  <Field
                    name="durationComments"
                    as="textarea"
                    rows="2"
                    style={formStyles.input}
                  />
                </div>

                {/* 6. Conduct/Character */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>6</span>
                    <p style={formStyles.question}>
                      How would you describe their conduct, character and associations?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(140px, 1fr))')}
                  >
                    {options.character.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.conductCharacter === opt)}
                      >
                        <Field
                          type="radio"
                          name="conductCharacter"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="conductCharacter"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>Comments (optional)</label>
                  <Field
                    name="conductComments"
                    as="textarea"
                    rows="2"
                    style={formStyles.input}
                  />
                </div>

                {/* 7. Honesty */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>7</span>
                    <p style={formStyles.question}>
                      Do you consider them to be honest?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div
                    style={formStyles.choiceGrid('repeat(auto-fit, minmax(140px, 1fr))')}
                  >
                    {options.honest.map((opt) => (
                      <label key={opt} style={formStyles.choice(values.isHonest === opt)}>
                        <Field
                          type="radio"
                          name="isHonest"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="isHonest"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* 8. Recommendation */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>8</span>
                    <p style={formStyles.question}>
                      Would you recommend them for employment with us, and why?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid('repeat(2, 1fr)')}>
                    {options.recommend.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.recommendEmployment === opt)}
                      >
                        <Field
                          type="radio"
                          name="recommendEmployment"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="recommendEmployment"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>Comments (optional)</label>
                  <Field
                    name="recommendComments"
                    as="textarea"
                    rows="2"
                    style={formStyles.input}
                  />
                </div>

                {/* 9. Chosen to Stand */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>9</span>
                    <p style={formStyles.question}>
                      Do you choose to stand as their guarantor?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid('repeat(2, 1fr)')}>
                    {options.chosenToStand.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.chosenToStand === opt)}
                      >
                        <Field
                          type="radio"
                          name="chosenToStand"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="chosenToStand"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* General Comments */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>💬</span>
                    <p style={formStyles.question}>
                      Anything else you&apos;d like to add?
                    </p>
                  </div>
                  <Field
                    name="generalComments"
                    as="textarea"
                    rows="4"
                    placeholder="Any final remarks..."
                    style={formStyles.input}
                  />
                </div>

                {/* Document Section */}
                <div style={formStyles.cardLast}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>🪪</span>
                    <div>
                      <p style={formStyles.question}>
                        Upload a valid means of identification{' '}
                        <span style={formStyles.required}>*</span>
                      </p>
                      <p style={{ ...formStyles.helperText, color: form.slate }}>
                        Government-issued only — NIN, driver&apos;s license, international
                        passport, or voter&apos;s card.
                      </p>
                    </div>
                  </div>
                  <FormUploader
                    accept={[
                      'image/png',
                      'image/jpg',
                      'image/jpeg',
                      'application/pdf',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'application/vnd.ms-powerpoint',
                      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                    ]}
                    handleUpload={(files) => setFieldValue('idCard', files[0])}
                    text="Choose File"
                    file={values.idCard}
                    form
                  />
                  <ErrorMessage
                    name="idCard"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>
              </div>

              {/* Final Certification & Submit */}
              <div style={formStyles.agreementCard}>
                <Field
                  type="checkbox"
                  name="agreement"
                  id="agreementCheck"
                  style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                    marginTop: '3px'
                  }}
                />
                <div>
                  <label
                    htmlFor="agreementCheck"
                    style={{
                      cursor: 'pointer',
                      lineHeight: '1.5',
                      color: form.ink,
                      fontWeight: 600,
                      fontSize: '1.18rem'
                    }}
                  >
                    By checking this box <span style={formStyles.required}>*</span>, I
                    confirm that all information provided and the live photograph I have
                    uploaded is a true, current, and unaltered image of myself, and I
                    consent to Workforce Group capturing, processing, and storing this
                    image solely to verify my identity against the means of identification
                    I have provided. I understand this data will be handled securely in
                    accordance with the Nigeria Data Protection Act, 2023, and will not be
                    used for any unrelated purpose without my consent.
                  </label>
                  <ErrorMessage
                    name="agreement"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>
              </div>

              <div style={formStyles.submitBar}>
                <button type="submit" style={formStyles.submitButton}>
                  {showLoader ? <Loader /> : 'Submit Guarantor Form'}
                </button>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default GuarantorForm;
